import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useParams,
  Outlet,
} from "react-router-dom";
import ReactDOM from "react-dom/client";
import { supabase } from "./supabaseClient";
import "./Style/App.scss";
import HomePlayersList from "./Components/HomePage/HomePlayersList";
import HomePage from "./Components/HomePage/HomePage";
import AdminHome from "./Components/Admin/AdminHome";
import AdminLogin from "./Components/Admin/AdminLogin";
import HomeLiveTV from "./Components/HomePage/HomeLiveTV";
import Rewards from "./Components/Pages/Rewards";
import Sponsors from "./Components/Pages/Sponsors";
import FucaParty from "./Components/Pages/FucaParty";

import HomeScorers from "./Components/HomePage/HomeScorers";
import HomeChampionship from "./Components/HomePage/HomeChampionship";
import PrivacyPolicy from "./Components/Pages/PrivacyPolicy";
import TermsOfUse from "./Components/Pages/TermsOfUse";
// import Rules from "./Components/Pages/Rules";

import BelaTurnir from "./Components/Pages/BelaTurnir";
import PageNotFound from "./Components/Pages/PageNotFound";

import WebInProgress from "./Components/FSApp/WebInProgress";
import FSAdminHome from "./Components/FucaScoreAdmin/FSAdminHome";
import FSAdminAddTurnir from "./Components/FucaScoreAdmin/FSAdminAddTurnir";
import FSHome from "./Components/FSApp/FSHome";
import FSPokreniTurnir from "./Components/FSApp/FSPokreniTurnir";
import ResetPassword from "./Components/Pages/ResetPassword";
import NogoTenisTurnir from "./Components/Pages/NogoTenisTurnir";
import FSAdminAddTeams from "./Components/FucaScoreAdmin/FSAdminAddTeams";

const TurnirLayout = () => {
  const { turnirSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exists, setExists] = useState(false);

  useEffect(() => {
    const checkTurnir = async () => {
      const { data } = await supabase
        .from("admin_turniri")
        .select("id")
        .eq("slug", turnirSlug)
        .single();

      if (data) {
        setExists(true);
      } else {
        setExists(false);
      }
      setLoading(false);
    };

    checkTurnir();
  }, [turnirSlug]);

  if (loading) return null;

  if (!exists) {
    return (
      <PageNotFound
        onGoHome={() => navigate("/")}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  return <Outlet />;
};

const App = () => {
  const [session, setSession] = useState(null);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      if (!isLocalhost) {
        // POVLAČIMO DODATNE STUPCE IZ BAZE:
        const { data } = await supabase
          .from("site_settings")
          .select(
            "maintenance_mode, maintenance_message, maintenance_badge, facebook_url, instagram_url, tiktok_url",
          )
          .eq("id", 1)
          .single();

        if (data && data.maintenance_mode) {
          setIsMaintenance(true);
          // Spremamo sve u jedan objekt
          setMaintenanceMessage(data.maintenance_message);
          window.maintenanceData = data;
        }
      }
    };

    checkMaintenanceMode();
    // 1. Dohvati session pri učitavanju
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    // 2. Prati promjene (login/logout)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (isMaintenance) {
    const currentPath = window.location.pathname.toLowerCase();
    const isBackendRoute = currentPath.includes("/admin");

    if (!isBackendRoute) {
      const d = window.maintenanceData || {};
      return (
        <WebInProgress
          badge={d.maintenance_badge}
          message={maintenanceMessage}
          socials={{
            facebook: d.facebook_url,
            instagram: d.instagram_url,
            tiktok: d.tiktok_url,
          }}
        />
      );
    }
  }

  const PageNotFoundWrapper = () => {
    const navigate = useNavigate();
    return (
      <PageNotFound
        onGoHome={() => navigate("/")}
        onGoBack={() => navigate(-1)}
      />
    );
  };

  return (
    <BrowserRouter>
      <Routes>
        {/* OPĆE / GLAVNE RUTE */}
        <Route path="/" element={<FSHome />} />
        <Route path="/stranica-u-izradi" element={<WebInProgress />} />

        <Route path="/pokreni-turnir" element={<FSPokreniTurnir />} />
        {/*   <Route path="/pravila-turnira" element={<Rules />} /> */}
        <Route path="/politika-privatnosti" element={<PrivacyPolicy />} />
        <Route path="/uvjeti-korištenja" element={<TermsOfUse />} />

        {/* FS ADMIN */}
        <Route
          path="/fucascore-admin"
          element={session ? <FSAdminHome /> : <Navigate to="/admin-login" />}
        />
        <Route
          path="/fs-add-turnir"
          element={
            session ? <FSAdminAddTurnir /> : <Navigate to="/admin-login" />
          }
        />
        <Route
          path="/fs-admin-all-teams"
          element={
            session ? <FSAdminAddTeams /> : <Navigate to="/admin-login" />
          }
        />
        <Route path="/resetiraj-lozinku" element={<ResetPassword />} />
        {/* LOGIN I BACKEND */}
        <Route
          path="/admin-login"
          element={!session ? <AdminLogin /> : <Navigate to="/admin-panel" />}
        />
        <Route
          path="/admin-panel"
          element={
            session ? (
              <AdminHome session={session} />
            ) : (
              <Navigate to="/admin-login" />
            )
          }
        />

        <Route element={<TurnirLayout />}>
          <Route path="/:turnirSlug" element={<HomePage />} />
          <Route path="/:turnirSlug/tv" element={<HomeLiveTV />} />
          <Route path="/:turnirSlug/strijelci" element={<HomeScorers />} />
          <Route path="/:turnirSlug/zavrsnica" element={<HomeChampionship />} />
          <Route path="/:turnirSlug/bela-turnir" element={<BelaTurnir />} />
          <Route
            path="/:turnirSlug/nogotenis-turnir"
            element={<NogoTenisTurnir />}
          />
          <Route path="/:turnirSlug/nagrade" element={<Rewards />} />
          <Route path="/:turnirSlug/sponzori" element={<Sponsors />} />
          <Route path="/:turnirSlug/dogadaji" element={<FucaParty />} />
          <Route
            path="/:turnirSlug/popis-igraca"
            element={<HomePlayersList />}
          />
        </Route>
        {/* FALLBACK ZA SVE OSTALO */}
        <Route path="*" element={<PageNotFoundWrapper />} />
      </Routes>
    </BrowserRouter>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
